/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';

import {
  Amenities,
  AmenityTitle,
  DetailBoxSmall,
  DetailBoxTitle,
  Error,
  Li,
  PriceSoldout,
  RoomNameBox,
  Ul
} from '@this/components/hotel/hotel_pc.template';
import type { HotelResponse } from '@this/components/reserve_trip/select/hotel_box/hotel_candidate_box';
import { styled } from '@this/constants/themes';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type Hotel from '@this/domain/hotel/hotel';
import type { HotelBedType } from '@this/domain/hotel/hotel';
import { HOTEL_PLAN_POINT_NOTICE } from '@this/domain/hotel_element2';
import type HotelList from '@this/domain/hotel/hotel_list';
import type HotelStayPlan from '@this/domain/hotel/hotel_stay_plan';
import MarginType from '@this/domain/organization/margin_type2';
import type { HotelJson } from '@this/domain/select_repository';
import type User from '@this/domain/user/user';
import A from '@this/shared/atoms/a';
import HotelMap from '@this/shared/hotel_map/hotel_map';
import Text from '@this/shared/text/text';
import TranslateIgnoreText from '@this/shared/text/translate_ignore_text';
import SimpleLoading from '@this/src/components/shared/simple_loading/simple_loading';
import { Modal, ModalBody, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import { Radio, RadioGroup } from '@this/shared/ui/inputs/radio';
import { Fetcher, toDomesticTel } from '@this/src/util';
import { BedTypeNotice } from '../../../shared/bed_type_notice/bed_type_notice';

import { NoteForPrice } from '../hotel_box/hotel_box_parts';
import StayPlanWarning from '../shared/stay_plan_warning';

interface Props {
  destLocation?: { lat: number; lng: number } | null;
  me?: User | null;
  user: User;
  hotel: Hotel | null;
  hotels: HotelList;
  hotelPriceLimit: number | null;
  showModal: boolean;
  searchQueryId: number | null;
  serviceId: number | undefined;
  handleChangeHotelStayPlan: (
    hotel: HotelJson,
    selectedPlan: HotelStayPlan,
    showFee: boolean,
    marginType: MarginType | undefined
  ) => void;
  submitButtonLabel: string | null | undefined;
  onClose: () => void;
}

const HotelDetailModalPcTemplate = observer(
  ({
    destLocation,
    me,
    hotel,
    hotelPriceLimit,
    showModal,
    searchQueryId,
    serviceId,
    handleChangeHotelStayPlan,
    submitButtonLabel,
    onClose
  }: Props) => {
    const { theme } = useAITTheme();
    const moreStayPlansStyles = useMemo(() => getMoreStayPlansStyles(theme), [theme]);
    const [showMoreStayPlans, setShowMoreStayPlans] = useState<boolean>(false);
    const [showMorePropertyAmenities, setShowMorePropertyAmenities] = useState<boolean>(false);
    const [showMoreRoomAmenities, setShowMoreRoomAmenities] = useState<boolean>(false);
    const [selectedStayPlan, setSelectedStayPlan] = useState<HotelStayPlan | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [planDetail, setPlanDetail] = useState<string | null | undefined>(hotel?.plan_detail || null);
    const [fetchedGoogleReviews, setFetchedGoogleReviews] = useState<HotelJson['google_reviews']>(undefined);

    const fetchPlanDetail = useCallback(
      async (selectedStayPlan: HotelStayPlan) => {
        if (hotel) {
          setLoading(true);
          const changedPlanDetail = await hotel.fetchPlanDetail(selectedStayPlan);
          setPlanDetail(changedPlanDetail);
          setLoading(false);
        }
      },
      [hotel]
    );

    useEffect(() => {
      if (hotel) {
        const stayPlan = hotel.stay_plans.find(s => s.id === hotel.selected_stay_plan_id) || null;
        setSelectedStayPlan(stayPlan);
      }
    }, [hotel]);

    useEffect(() => {
      if (selectedStayPlan) {
        fetchPlanDetail(selectedStayPlan);
      }
    }, [selectedStayPlan, fetchPlanDetail]);

    useEffect(() => {
      if (!showModal || !hotel?.hotel_master_id) return;
      setFetchedGoogleReviews(undefined);
      Fetcher.get<{ google_reviews: HotelJson['google_reviews'] }>(
        `/hotel_masters/${hotel.hotel_master_id}/google_reviews`
      )
        .then(result => {
          setFetchedGoogleReviews(result.google_reviews);
        })
        .catch(() => {});
    }, [showModal, hotel?.hotel_master_id]);

    const handleImageHover = (i: number) => () => {
      if (hotel) {
        return hotel.selectImage(i);
      }
      return undefined;
    };

    const togglePropertyAmenities = () => {
      setShowMorePropertyAmenities(!showMorePropertyAmenities);
    };

    const getPropertyAmenities = () => {
      const amenities = hotel && hotel.propertyAmenities();
      if (showMorePropertyAmenities) {
        return amenities || [];
      }
      return (amenities && amenities.slice(0, 10)) || [];
    };

    const toggleRoomAmenities = () => {
      setShowMoreRoomAmenities(!showMoreRoomAmenities);
    };
    const getRoomAmenities = () => {
      const amenities = hotel && hotel.roomAmenities();
      if (showMoreRoomAmenities) {
        return amenities || [];
      }

      return (amenities && amenities.slice(0, 10)) || [];
    };

    const toggleShowMoreStayPlans = () => {
      setShowMoreStayPlans(!showMoreStayPlans);
    };

    const handleClose = () => {
      setShowMoreStayPlans(false);
      onClose();
    };

    const handleClickStayPlan = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (hotel) {
        hotel.selected_stay_plan_id = e.target.value;
        const stayPlan = hotel.stay_plans.find(s => s.id === hotel.selected_stay_plan_id) || null;
        setSelectedStayPlan(stayPlan);
      }
      return undefined;
    };

    const handleChangeStayPlan = () => {
      if (hotel) {
        onClose();
        hotel.setHotelLoading(true);
        if (selectedStayPlan) {
          try {
            Fetcher.put<HotelResponse>(`/hotels/${hotel.id}/change_stay_plan`, {
              stay_plan_id: selectedStayPlan.id
            }).then(result => {
              let marginType: MarginType | undefined;
              if (result.margin_type) {
                marginType = new MarginType(result.margin_type);
              }

              handleChangeHotelStayPlan(result.hotel, selectedStayPlan, result.show_fee, marginType);
            });
          } finally {
            hotel.setHotelLoading(false);
          }
        }
      }
      return undefined;
    };

    const enableMultipleHotelPlans = () => {
      if (hotel?.isForeign()) {
        return me?.show_multiple_hotel_plans_foreign;
      }
      return me?.show_multiple_hotel_plans_domestic;
    };

    const googleReviews = fetchedGoogleReviews !== undefined ? fetchedGoogleReviews : hotel?.google_reviews;

    return (
      <Modal open={showModal} onClose={handleClose} size="large">
        <ModalHeader>プラン選択</ModalHeader>
        <ModalBody>
          {hotel == null ? (
            <LoadingSpan>
              <div data-wovn-ignore className="cssloader">
                Loading...
              </div>
            </LoadingSpan>
          ) : (
            <>
              <TopArea key={0}>
                <div>
                  <Title data-wovn-ignore>{hotel.name}</Title>
                  <Address data-wovn-ignore>{hotel.address}</Address>
                </div>
                {hotel.sold_out && <Soldout>※たった今他の方が予約されたため、空室がなくなりました。</Soldout>}
              </TopArea>
              <BodyArea key={1}>
                <BodyAreaIn>
                  <Row>
                    <Column>
                      <MainImageArea>
                        <MainImage style={{ backgroundImage: `url(${hotel.currentImage()})` }} />
                        <MainThumbs>
                          {hotel.images?.slice(0, 26).map((image, i) => (
                            <MainThumb
                              key={image || `thumb_${i}`}
                              selected={hotel.currentImage() === image}
                              style={{ backgroundImage: `url(${image})` }}
                              onMouseOver={handleImageHover(i)}
                            />
                          ))}
                        </MainThumbs>
                      </MainImageArea>
                    </Column>
                    <Column>
                      <MapArea>
                        {destLocation && (
                          <HotelMap
                            hotel={hotel}
                            destLocation={destLocation}
                            serviceId={serviceId}
                            searchQueryId={searchQueryId}
                          />
                        )}
                        <InfoArea>
                          <table className="key-value-table">
                            <tbody>
                              <tr>
                                <td className="key-value-table__label">目的地まで</td>
                                <td className="key-value-table__separator">：</td>
                                <td data-wovn-ignore className="key-value-table__value">
                                  {hotel.walkMinuteText()}
                                </td>
                              </tr>
                              <tr>
                                <td className="key-value-table__label">
                                  最寄駅まで{hotel.nearestStationNameText()}
                                </td>
                                <td className="key-value-table__separator">：</td>
                                <td data-wovn-ignore className="key-value-table__value">
                                  {hotel.stationDistanceText()}
                                </td>
                              </tr>
                              <tr>
                                <td className="key-value-table__label">チェックイン</td>
                                <td className="key-value-table__separator">：</td>
                                <td data-wovn-ignore className="key-value-table__value">
                                  {hotel.checkinText()}
                                </td>
                              </tr>
                              <tr>
                                <td className="key-value-table__label">チェックアウト</td>
                                <td className="key-value-table__separator">：</td>
                                <td data-wovn-ignore className="key-value-table__value">
                                  {hotel.checkoutText()}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <PriceArea>
                            {hotel.sold_out ? (
                              <PriceSoldout>空室なし</PriceSoldout>
                            ) : (
                              <>
                                <Price key={0}>
                                  <span>ホテル料金合計：</span>
                                  <span data-wovn-ignore>{utils.digits(hotel.totalPrice())}</span>
                                  <span>円</span>
                                </Price>
                                <TranslateIgnoreText text={hotel.priceDetailText()} />
                                {hotel.showCancelStatus(selectedStayPlan) && (
                                  <Error>{`※キャンセル：${hotel.refund}`}</Error>
                                )}
                              </>
                            )}
                          </PriceArea>
                        </InfoArea>
                        {planDetail && (
                          <Annotation>
                            ※プランごとのチェックイン・チェックアウト時間についてはプラン詳細をご確認ください。
                          </Annotation>
                        )}
                      </MapArea>
                    </Column>
                  </Row>
                  {hotel.tel && (
                    <>
                      <div style={telTitleStyle}>電話番号</div>
                      <div style={telDisplayStyle} data-wovn-ignore>
                        {toDomesticTel(hotel.tel)}
                      </div>
                    </>
                  )}
                  {selectedStayPlan !== null && (
                    <Row>
                      <SelectedStayPlanBox>
                        <RowTitle>
                          選択中のプラン
                          <DetailBoxSmall style={{ marginLeft: '6px' }}>
                            <Text text={HOTEL_PLAN_POINT_NOTICE} />
                          </DetailBoxSmall>
                        </RowTitle>
                        <SelectedStayPlanContentArea>
                          <StayPlanDetailArea>
                            <StayPlanNameRow>
                              <StayPlanName>{selectedStayPlan.name || '-'}</StayPlanName>
                              <StayPlanWarning restricted={selectedStayPlan.restricted ?? false} />
                            </StayPlanNameRow>
                            <StayPlanAmenities>
                              {selectedStayPlan.smoke !== null && (
                                <StayPlanSmokeType>{selectedStayPlan.smoke ? '喫煙' : '禁煙'}</StayPlanSmokeType>
                              )}
                              <RoomNameBox>{selectedStayPlan.room_name}</RoomNameBox>
                              <RoomBedTypes>
                                <ul>
                                  {selectedStayPlan.bed_types?.map((b: HotelBedType, i) => (
                                    <li key={b.id ?? b.description ?? `selected_bed_${i}`}>{b.description}</li>
                                  ))}
                                </ul>
                              </RoomBedTypes>
                              <StayPlanCheckinCheckout>
                                チェックイン：
                                {selectedStayPlan.checkin_time || hotel.checkinText()}
                              </StayPlanCheckinCheckout>
                            </StayPlanAmenities>
                            <BedTypeNotice bedTypes={selectedStayPlan.bed_types} />
                          </StayPlanDetailArea>
                          <StayPlanPriceBox>
                            <NoteForPrice
                              price={selectedStayPlan.total_fee || 0}
                              stayDays={hotel.stay_days}
                              roomNum={hotel.roomnum ? parseInt(hotel.roomnum, 10) : null}
                              hotelPriceLimit={hotelPriceLimit}
                            />
                            <div>
                              <span>{utils.digits(selectedStayPlan.total_fee || 0)}円</span>
                              <StayPlanPriceDetail>
                                {' '}
                                / {hotel.stay_days}泊{hotel.roomnum}室
                              </StayPlanPriceDetail>
                            </div>
                          </StayPlanPriceBox>
                        </SelectedStayPlanContentArea>
                      </SelectedStayPlanBox>
                    </Row>
                  )}
                  {hotel.stay_plans.length > 1 && enableMultipleHotelPlans() ? (
                    <>
                      {showMoreStayPlans && (
                        <>
                          <Row>
                            <StayPlanListBox>
                              <RadioGroup
                                layout="vertical"
                                onChange={handleClickStayPlan}
                                value={selectedStayPlan && selectedStayPlan.id}
                              >
                                {hotel.stay_plans.map((stay_plan: any, i: number) => (
                                  <StayPlanList key={stay_plan.id}>
                                    <Radio key={stay_plan.id} value={stay_plan.id}>
                                      <StayPlanContentArea>
                                        <StayPlanDetailArea>
                                          <StayPlanNameRow>
                                            <StayPlanName>{stay_plan.name || '-'}</StayPlanName>
                                            <StayPlanWarning restricted={stay_plan.restricted ?? false} />
                                          </StayPlanNameRow>
                                          <StayPlanAmenities>
                                            {stay_plan.smoke !== null && (
                                              <StayPlanSmokeType>
                                                {stay_plan.smoke ? '喫煙' : '禁煙'}
                                              </StayPlanSmokeType>
                                            )}
                                            <RoomNameBox>{stay_plan.room_name}</RoomNameBox>
                                            <RoomBedTypes>
                                              <ul>
                                                {stay_plan.bed_types.map((b: HotelBedType, idx: number) => (
                                                  <li key={b.description || `plan_bed_${idx}`}>{b.description}</li>
                                                ))}
                                              </ul>
                                            </RoomBedTypes>
                                            <StayPlanCheckinCheckout>
                                              チェックイン：
                                              {stay_plan.checkin_time || hotel.checkinText()}
                                            </StayPlanCheckinCheckout>
                                          </StayPlanAmenities>
                                          <BedTypeNotice bedTypes={stay_plan.bed_types} />
                                        </StayPlanDetailArea>
                                        <StayPlanPriceBox>
                                          <NoteForPrice
                                            price={stay_plan.total_fee || 0}
                                            stayDays={hotel.stay_days}
                                            roomNum={hotel.roomnum ? parseInt(hotel.roomnum, 10) : null}
                                            hotelPriceLimit={hotelPriceLimit}
                                          />
                                          <div>
                                            <span>{utils.digits(stay_plan.total_fee || 0)}円</span>
                                            <StayPlanPriceDetail>
                                              {' '}
                                              / {hotel.stay_days}泊{hotel.roomnum}室
                                            </StayPlanPriceDetail>
                                          </div>
                                        </StayPlanPriceBox>
                                      </StayPlanContentArea>
                                    </Radio>
                                  </StayPlanList>
                                ))}
                              </RadioGroup>
                            </StayPlanListBox>
                          </Row>
                          <Row>
                            <Column>
                              <MoreBoxHiddenButton onClick={toggleShowMoreStayPlans}>閉じる</MoreBoxHiddenButton>
                            </Column>
                            <Column>
                              <MoreBoxSelectButton onClick={() => handleChangeStayPlan()}>
                                {submitButtonLabel || 'このプランで決定'}
                              </MoreBoxSelectButton>
                            </Column>
                          </Row>
                        </>
                      )}
                      {!showMoreStayPlans && (
                        <div style={moreStayPlansStyles.row}>
                          <span style={moreStayPlansStyles.link} onClick={toggleShowMoreStayPlans}>
                            その他のプランを見る▼
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <></>
                  )}

                  <Row>
                    <Column>
                      {(loading || planDetail) && (
                        <DetailBox>
                          <DetailBoxTitle>宿泊プラン詳細</DetailBoxTitle>
                          <DetailBoxSmall>
                            {loading ? <SimpleLoading /> : <Text text={planDetail} />}
                          </DetailBoxSmall>
                        </DetailBox>
                      )}
                      <DetailBox>
                        <Amenities>
                          <AmenityTitle>施設の設備</AmenityTitle>
                          <Ul>
                            {getPropertyAmenities().map((a, i) => (
                              <Li key={a || `property_amenity_${i}`}>{a}</Li>
                            ))}
                          </Ul>
                          {getPropertyAmenities().length >= 10 && (
                            <ToggleLink onClick={togglePropertyAmenities}>
                              {showMorePropertyAmenities ? '閉じる' : 'もっと見る'}
                            </ToggleLink>
                          )}
                        </Amenities>
                      </DetailBox>
                    </Column>
                    <Column>
                      <Amenities>
                        <AmenityTitle>部屋の設備</AmenityTitle>
                        <Ul>
                          {getRoomAmenities().map((a, i) => (
                            <Li key={a || `room_amenity_${i}`}>{a}</Li>
                          ))}
                        </Ul>
                        {getRoomAmenities().length >= 10 && (
                          <ToggleLink onClick={toggleRoomAmenities}>
                            {showMoreRoomAmenities ? '閉じる' : 'もっと見る'}
                          </ToggleLink>
                        )}
                      </Amenities>
                    </Column>
                    <Column>
                      <DetailBox>
                        <DetailBoxTitle>キャンセルポリシー</DetailBoxTitle>
                        <DetailBoxSmall>
                          <Text text={selectedStayPlan?.cancellation_policy || hotel.cancelPolicy()} />
                        </DetailBoxSmall>
                      </DetailBox>
                      {hotel.specialCheckinInstructions() && (
                        <DetailBox>
                          <DetailBoxTitle>備考</DetailBoxTitle>
                          <DetailBoxSmall
                            data-wovn-ignore
                            dangerouslySetInnerHTML={{ __html: hotel.specialCheckinInstructions() || '' }}
                          />
                        </DetailBox>
                      )}
                      <DetailBox>
                        <DetailBoxSmall dangerouslySetInnerHTML={{ __html: hotel.checkinInstructions() }} />
                      </DetailBox>
                      {hotel.parkingInformation && (
                        <DetailBox>
                          <DetailBoxTitle>駐車場に関する情報</DetailBoxTitle>
                          <DetailBoxSmall>
                            <TranslateIgnoreText text={hotel.parkingInformation} />
                          </DetailBoxSmall>
                        </DetailBox>
                      )}
                    </Column>
                  </Row>
                  {googleReviews?.reviews && googleReviews.reviews.length > 0 && (
                    <Row>
                      <Column>
                        <DetailBox>
                          <DetailBoxTitle>
                            クチコミ
                            {googleReviews.rating != null && (
                              <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 'normal' }}>
                                Google評価：★{googleReviews.rating.toFixed(1)}
                              </span>
                            )}
                          </DetailBoxTitle>
                          <DetailBoxSmall>
                            {googleReviews.reviews.map((review, i) => (
                              <div
                                key={`review_${i}`}
                                style={{
                                  borderBottom: i < googleReviews!.reviews!.length - 1 ? '1px solid #eee' : 'none',
                                  padding: '10px 0'
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '4px',
                                    fontSize: '12px'
                                  }}
                                >
                                  <span style={{ color: '#f5a623', fontSize: '13px' }}>
                                    {'★'.repeat(review.rating)}
                                    {'☆'.repeat(5 - review.rating)}
                                  </span>
                                  <span data-wovn-ignore style={{ fontWeight: 'bold' }}>
                                    {review.author_name}
                                  </span>
                                  <span data-wovn-ignore style={{ color: '#999' }}>
                                    {review.relative_time_description}
                                  </span>
                                </div>
                                <div
                                  data-wovn-ignore
                                  style={{ fontSize: '12px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}
                                >
                                  {review.text}
                                </div>
                              </div>
                            ))}
                          </DetailBoxSmall>
                        </DetailBox>
                      </Column>
                    </Row>
                  )}
                </BodyAreaIn>
              </BodyArea>
            </>
          )}
        </ModalBody>
      </Modal>
    );
  }
);

const TopArea = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.grayBorderColor};
  padding: 12px 15px;
`;

const Title = styled.div`
  font-size: 18px;
`;

const Address = styled.div`
  font-size: 12px;
`;

const Soldout = styled.div`
  margin-left: auto;
  color: ${props => props.theme.redColor};
`;

const BodyArea = styled.div`
  width: 100%;
  padding: 20px;
`;

const BodyAreaIn = styled.div``;

const mainThumbWidth = 36;
const mainImageWidth = mainThumbWidth * 13;
const imageBorderWidth = 3;
const mapWidth = 452;

const MainImageArea = styled.div`
  flex-grow: 1;
  max-width: ${`${mainImageWidth + imageBorderWidth * 2}px`};
  height: 367px;
  border: ${`${imageBorderWidth}px`} solid ${props => props.theme.hotelImageBorderColor};
  border-radius: ${imageBorderWidth};
`;

const MainImage = styled.div`
  max-width: ${`${mainImageWidth}px`};
  width: 100%;
  height: 288px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
`;

const MainThumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: ${`${mainImageWidth}px`};
  background: ${props => props.theme.grayColorLight};
`;

const MainThumb = styled.div<{ selected: boolean }>`
  width: ${`${mainThumbWidth}px`};
  height: ${`${mainThumbWidth}px`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;

  ${props =>
    !props.selected &&
    `
    background-blend-mode: color;
    background-color: rgba(255, 255, 255, 0.7);
  `}
`;

const MapArea = styled.div`
  max-width: ${`${mapWidth}px`};
  width: 100%;
  margin-left: auto;
`;

const InfoArea = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${props => props.theme.grayColorLight};
  font-size: 12px;
  flex-wrap: wrap;
  justify-content: left;
`;

const PriceArea = styled.div`
  background: #fff;
  padding: 7px 10px;
  margin-left: auto;
`;

const Price = styled.div`
  font-size: 14px;
  color: ${props => props.theme.moneyColor};
`;

const ToggleLink = styled(A)`
  font-size: 10px;
`;

const Annotation = styled.p`
  font-size: 12px;
  padding: 0 12px;
`;

export const DetailBox = styled.div`
  margin-bottom: 20px;
  padding-right: 20px;
  width: 100%;
`;

export const SelectedStayPlanBox = styled.div`
  width: 100%;
`;

export const SelectedStayPlanContentArea = styled.div`
  border: 3px solid #eee;
  border-color: ${props => props.theme.accentColor};
  display: inline-flex;
  width: 100%;
  gap: 10px;
  padding: 10px;
  justify-content: center;
  align-items: center;
`;

export const StayPlanListBox = styled.div`
  width: 100%;
`;

export const StayPlanList = styled.div`
  border: 1px solid #eee;
  width: 100%;
  padding: 0 0 0 10px;
  margin-bottom: 5px;
  display: inline-flex;

  & > label {
    width: 100%;
  }

  & .MuiTypography-root {
    width: 100%;
  }
`;

export const StayPlanContentArea = styled.div`
  display: inline-flex;
  width: 100%;
  gap: 10px;
  padding: 10px;
  justify-content: center;
  align-items: center;
`;

export const StayPlanDetailArea = styled.div`
  flex-grow: 1;
`;

export const StayPlanPriceBox = styled.div`
  display: inline-flex;
  flex-direction: column;
  flex-basis: 150px;
  color: ${props => props.theme.accentColor};
  font-weight: bold;
`;

export const MoreBox = styled.div`
  display: block;
  margin: auto;
`;

export const BoxButtonArea = styled.div`
  justify-content: center;
  display: inline-flex;
  flex-direction: column;
  margin: 10px auto auto auto;
`;

export const BoxButton = styled.div`
  align-items: center;

  width: 160px;
  height: 30px;
  text-align: center;
  color: ${props => props.theme.linkColor};
  background: white;
  border: 1px solid ${props => props.theme.linkColor};
  border-radius: 5px;
  padding: 2px;
  font-weight: bold;
  line-height: 28px;
  font-size: 11px;
  cursor: pointer;
`;

export const MoreBoxHiddenButton = styled(BoxButton)`
  margin-right: 10px;
  margin-left: auto;
  display: block;
`;

export const MoreBoxSelectButton = styled(BoxButton)`
  color: white;
  background: ${props => props.theme.linkColor};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  margin-top: 10px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  flex: 1;
`;

const RowTitle = styled.div`
  margin: 0 0 5px 0;
  display: flex;
  align-items: center;
  font-weight: bold;
`;

const RoomBedTypes = styled.div`
  display: flex;
  align-items: center;
  padding: 0 5px 0 5px;
`;

const StayPlanCheckinCheckout = styled.div`
  padding: 0 5px 0 5px;
`;

const StayPlanAmenities = styled.div`
  display: inline-flex;
  font-size: 10px;
  font-weight: normal;
  text-indent: -5px;
`;

const StayPlanSmokeType = styled.div`
  padding: 0 5px 0 5px;
`;

const StayPlanNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StayPlanName = styled.div`
  font-weight: bold;
`;

const StayPlanPriceDetail = styled.span`
  font-weight: normal;
  height: 100%;
`;

const LoadingSpan = styled.span`
  width: 20%;
  top: -0.5em;
`;

const telTitleStyle: React.CSSProperties = {
  marginTop: '10px',
  marginBottom: '0',
  fontWeight: 'bold'
};

const telDisplayStyle: React.CSSProperties = {
  paddingLeft: '10px',
  fontSize: '12px',
  fontWeight: 'normal'
};

const getMoreStayPlansStyles = (theme: AITTheme) => ({
  row: {
    display: 'flex' as const,
    justifyContent: 'flex-end' as const,
    width: '100%',
    marginTop: 8
  },
  link: {
    color: theme.linkColor,
    cursor: 'pointer' as const,
    fontSize: 13
  }
});

export default HotelDetailModalPcTemplate;
